import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { COPY, getInitialLocale, LOCALE_STORAGE_KEY } from '../lib/locale';

const LocaleContext = createContext(null);
const DEFAULT_LOCALE_CONTEXT = {
  locale: 'pt',
  setLocale: () => {},
  t: (key) => COPY.pt[key] || key,
};

function resolveRouteLocale(pathname, hostname, storedLocale = '') {
  const isSpanishRoute = pathname === '/es' || pathname.startsWith('/es/');
  if (isSpanishRoute) return 'es';
  const isPlatformRoute = /^\/(?:dashboard|gestao|conta|admin|redefinir-senha)(?:\/|$)/.test(pathname) || /^\/avaliacao\/[^/]+$/.test(pathname);
  if (isPlatformRoute && (storedLocale === 'pt' || storedLocale === 'es')) return storedLocale;
  return getInitialLocale(hostname, storedLocale);
}

export function LocaleProvider({ children }) {
  const location = useLocation();
  const [locale, setLocaleState] = useState(() => {
    if (typeof window === 'undefined') return location.pathname === '/es' || location.pathname.startsWith('/es/') ? 'es' : 'pt';
    let stored = '';
    try { stored = window.localStorage.getItem(LOCALE_STORAGE_KEY) || ''; } catch { /* default to host locale */ }
    return resolveRouteLocale(location.pathname, window.location.hostname, stored);
  });

  useEffect(() => {
    let stored = '';
    try { stored = window.localStorage.getItem(LOCALE_STORAGE_KEY) || ''; } catch { /* default to host locale */ }
    setLocaleState(resolveRouteLocale(window.location.pathname, window.location.hostname, stored));
  }, []);

  const setLocale = useCallback((nextLocale) => {
    const normalized = nextLocale === 'es' ? 'es' : 'pt';
    window.localStorage.setItem(LOCALE_STORAGE_KEY, normalized);
    setLocaleState(normalized);
  }, []);

  useEffect(() => {
    document.documentElement.lang = locale === 'es' ? 'es' : 'pt-BR';
  }, [locale]);

  const value = useMemo(() => ({ locale, setLocale, t: (key) => COPY[locale][key] || COPY.pt[key] || key }), [locale, setLocale]);
  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale() {
  const context = useContext(LocaleContext);
  return context || DEFAULT_LOCALE_CONTEXT;
}
